/**
 * A drill chain, as data: what was clicked at each level, where it landed, and
 * the words the menu and the tray print about it.
 *
 * PURE — no React, no fetching — so the rules that decide what a click offers
 * and what a landing asks can be tested without rendering anything. The
 * decisions themselves are `@clockwise/semantic-core`'s (`drillEligibility`,
 * `landingSpec`); this file strings them into a chain.
 *
 * See docs/reporting/drill-down.md.
 */
import {
  type DrillCandidate,
  type DrillCoordinate,
  type DrillLanding,
  drillEligibility,
  filterDimensionIds,
  findDimension,
  isDrillable,
  landingSpec,
  lendBasis,
  MAX_DRILL_DEPTH,
  type ReportModel,
  type ReportSpec,
  rankDrillTargets,
} from '@clockwise/semantic-core';
import { z } from 'zod';

/** The model's own drill — the rows behind the figure. */
export const ENTRIES = 'entries';

/** One level of a drill: the click that opened it, and where it went. */
export interface DrillStep {
  /** {@link ENTRIES}, or the id of the saved report landed on. */
  target: string;
  /** What the click pinned, by dimension id → key. */
  at: DrillCoordinate;
  /** What the reader saw at each pinned level — words only, never a filter. */
  labels: Record<string, string>;
  /** The measure clicked, on the model of the level it was clicked on. */
  measure: string;
  /** The figure as it was printed. */
  figure: string;
  /** How the figure was shown when it was a share (`pctRow`…), not itself. */
  showAs?: 'pctRow' | 'pctCol' | 'pctTotal';
}

// ---- The URL ---------------------------------------------------------------

const stepSchema = z.object({
  t: z.string().min(1).max(64),
  m: z.string().min(1).max(64),
  a: z.record(z.string().max(64), z.string().max(200).nullable()),
  // Links made before labels left the URL still parse; the labels are ignored.
  l: z.record(z.string().max(64), z.string().max(200)).optional(),
  f: z.string().max(64).default(''),
  s: z.enum(['pctRow', 'pctCol', 'pctTotal']).optional(),
});
const chainSchema = z.array(stepSchema).max(MAX_DRILL_DEPTH);

/**
 * `?drill=` — the chain, compact. JSON, base64url, so it survives a copy and
 * paste through chat clients that mangle brackets and quotes.
 */
export function encodeDrill(chain: readonly DrillStep[]): string | undefined {
  if (chain.length === 0) return undefined;
  const json = JSON.stringify(
    // KEYS ONLY. The labels are people's and customers' names, and a link
    // travels into histories, logs and chats; they are re-read on the way back
    // in (see `useReportDrill`).
    chain.map((s) => ({
      t: s.target,
      m: s.measure,
      a: s.at,
      f: s.figure,
      ...(s.showAs ? { s: s.showAs } : {}),
    })),
  );
  const bytes = new TextEncoder().encode(json);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * The chain a `?drill=` names, or none. ANYTHING wrong — a truncated paste, a
 * link from a build that encoded it differently — is no drill rather than an
 * error: the report underneath is still the page the link was to.
 */
export function decodeDrill(param: string | undefined): DrillStep[] {
  if (!param) return [];
  try {
    const padded = param.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
    const json = new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)));
    const parsed = chainSchema.safeParse(JSON.parse(json));
    if (!parsed.success) return [];
    return parsed.data.map((s) => ({
      target: s.t,
      measure: s.m,
      at: s.a,
      labels: {},
      figure: s.f,
      ...(s.s ? { showAs: s.s } : {}),
    }));
  } catch {
    return [];
  }
}

// ---- Replaying a chain -----------------------------------------------------

/**
 * The model a spec must be READ against — its own, with a drill's basis lent.
 *
 * The server lends the same measure when it runs the spec (see `spec.basis`);
 * the browser has to as well, or the table cannot name the column the reader
 * clicked through to.
 */
export function modelFor(
  models: readonly ReportModel[],
  spec: Pick<ReportSpec, 'model' | 'basis'>,
): ReportModel | undefined {
  const own = models.find((m) => m.id === spec.model);
  if (!own || !spec.basis) return own;
  const from = models.find((m) => m.id === spec.basis?.model);
  return from ? lendBasis(own, from, spec.basis.measure) : own;
}

/** What a saved report target resolves to while its definition loads. */
export type TargetDefinition = { name: string; spec: ReportSpec } | 'pending' | 'missing';

export interface DrillLevel {
  step: DrillStep;
  /** What the lineage calls this level. */
  name: string;
  /** The landing's model, with any basis lent. */
  model: ReportModel;
  landing: DrillLanding;
}

export interface ResolvedChain {
  levels: DrillLevel[];
  /** A saved report further down is still loading. */
  pending: boolean;
  /** Why the chain stops short of its end, when it does. */
  broken?: string;
}

/**
 * Replay a chain from the report it started on. Each level's landing is the
 * previous level asked a new question — so a change to the source report (a
 * new period on the bar) flows all the way down.
 */
export function resolveChain(
  models: readonly ReportModel[],
  source: { model: ReportModel; spec: ReportSpec },
  chain: readonly DrillStep[],
  definition: (reportId: string) => TargetDefinition,
): ResolvedChain {
  const levels: DrillLevel[] = [];
  let from = source;
  for (const step of chain) {
    let name: string;
    let landing: DrillLanding | null;
    if (step.target === ENTRIES) {
      landing = landingSpec(models, from, step.at, step.measure, { kind: 'entries' });
      name = entriesName(models, landing?.spec.model);
    } else {
      const found = definition(step.target);
      if (found === 'pending') return { levels, pending: true };
      if (found === 'missing') {
        return {
          levels,
          pending: false,
          broken: 'A report in this drill is no longer available to you.',
        };
      }
      landing = landingSpec(models, from, step.at, step.measure, {
        kind: 'report',
        spec: found.spec,
      });
      name = found.name;
    }
    const model = landing && modelFor(models, landing.spec);
    if (!landing || !model) {
      return { levels, pending: false, broken: 'This drill cannot be opened from here any more.' };
    }
    levels.push({ step, name, model, landing });
    from = { model, spec: landing.spec };
  }
  return { levels, pending: false };
}

/** "Timesheet entries" — what the model drill's landing is called. */
export function entriesName(models: readonly ReportModel[], modelId: string | undefined): string {
  const label = models.find((m) => m.id === modelId)?.label;
  return label ? `${label.replace(/s$/, '')} entries` : 'Entries';
}

// ---- The menu --------------------------------------------------------------

export interface DrillMenuPlan {
  /** Offer the model's own drill. */
  entries: boolean;
  targets: DrillCandidate[];
}

/**
 * What one click offers.
 *
 * `pinned` is CUMULATIVE — every dimension pinned anywhere up the chain, and
 * this click's — because each of those is a filter on the landing now, and a
 * target that would only re-split one of them adds nothing.
 */
export function planMenu(
  models: readonly ReportModel[],
  candidates: readonly DrillCandidate[],
  origin: {
    model: ReportModel;
    spec: ReportSpec;
    pinned: readonly string[];
    /** Saved-report ids already in the chain, the source report included. */
    chain: readonly string[];
  },
): DrillMenuPlan {
  if (!isDrillable(origin.model)) return { entries: false, targets: [] };
  const drillOrigin = {
    model: origin.model,
    pinned: origin.pinned,
    filtered: filterDimensionIds(origin.spec.filters),
    chain: origin.chain,
  };
  const entries = drillEligibility(models, drillOrigin, {
    id: ENTRIES,
    label: '',
    name: '',
    model: origin.model.drillTo?.report ?? origin.model.id,
    kind: 'detail',
    axes: [],
    filterDimensions: [],
  }).ok;

  // The report clicked on is in `chain`, so rule 4 already keeps it out.
  const kept = candidates.filter((c) => drillEligibility(models, drillOrigin, c).ok);
  return { entries, targets: rankDrillTargets(drillOrigin, kept) };
}

// ---- Words -----------------------------------------------------------------

const dimLabel = (models: readonly ReportModel[], id: string) => {
  for (const m of models) {
    const d = findDimension(m, id);
    if (d) return d.label;
  }
  return id;
};

/** "Under 6 months · Ravi Menon · Priya Nair · Aug 2026" */
export function coordinateWords(labels: Record<string, string>): string {
  const words = Object.values(labels);
  return words.length ? words.join(' · ') : 'The whole report';
}

/** "Groups by Customer · Employee × Billable", and what does not carry. */
export function targetDetail(models: readonly ReportModel[], c: DrillCandidate): string {
  if (c.kind === 'detail') return 'A listing — the rows in its columns and order.';
  const words = `Groups by ${c.axes.map((id) => dimLabel(models, id)).join(', ')}`;
  if (!c.filterDimensions.length) return words;
  const own = c.filterDimensions.map((id) => dimLabel(models, id)).join(', ');
  return `${words}. Its own filters (${own}) don’t apply here.`;
}

/** The filters the tray shows as carried in: every pin up the chain. */
export function trayChips(
  models: readonly ReportModel[],
  chain: readonly DrillStep[],
): { key: string; label: string; value: string; fresh?: boolean }[] {
  return chain.flatMap((step, level) =>
    Object.keys(step.at).map((id) => ({
      key: `${level}:${id}`,
      label: dimLabel(models, id),
      value: step.labels[id] ?? (step.at[id] === null ? 'no value' : String(step.at[id])),
      ...(level === chain.length - 1 && level > 0 ? { fresh: true } : {}),
    })),
  );
}
